import { stringify } from 'qs';
import { useMemo } from 'react';

import Locator from '../../../../../shared/Locator';
import { useParams, useQuery } from '../../../../contexts/RouterContext';
import { getProjectBaseURLFromParams } from '../../../../utils';
import type { SnippetsQueryParams } from '../../../../../shared/snippets';
import type { RevisionRouteParams } from '../../projectDashboardTypes';

/**
 * Custom hook that generates a shareable URL for the selected snippet.
 *
 * This hook handles the URL generation logic for snippet-related components,
 * creating a shareable URL (with origin) for the selected snippet.
 * It automatically uses current query parameters and route context to get the selected snippet ID.
 *
 * @param userRevisionLocator Optional locator string to specify the revision context for the snippet URL. If not provided, the current route params are used.
 * @returns Shareable URL string (empty string if no snippet selected)
 *
 * @example
 * ```typescript
 * // Use with current selected snippet from query params
 * const shareUrl = useSnippetUrl();
 *
 * ```
 */
const useSnippetUrl = (userRevisionLocator?: string): string => {
  const routeParams = useParams<RevisionRouteParams>();

  // If a specific userRevisionLocator is provided, use it to override the revision_id param.
  // Also, make a copy of routeParams to avoid mutating the actual URL.
  const routeParamsCopy = structuredClone(routeParams);
  if (userRevisionLocator) {
    const { revision } = Locator.parse(userRevisionLocator);
    if (revision) {
      routeParamsCopy.revision_id = revision;
    }
  }

  const [queryParams] = useQuery<SnippetsQueryParams>();

  return useMemo(() => {
    const selectedSnippetId = queryParams.selectedSnippetId;

    if (!selectedSnippetId || !routeParamsCopy) {
      return '';
    }

    const projectBaseURL = getProjectBaseURLFromParams(routeParamsCopy);
    const baseURL = `${projectBaseURL}/browse/snippets/${encodeURIComponent(selectedSnippetId)}`;
    const queryString = stringify(queryParams);
    const url = queryString ? `${baseURL}?${queryString}` : baseURL;

    return `${window.location.origin}${url}`;
  }, [routeParamsCopy, queryParams]);
};

export default useSnippetUrl;
